import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Loader2, Users, UserCheck } from "lucide-react";
import { JudgeOptionLabel } from "./JudgeOptionLabel";
import { downloadActFromUrl } from "./actDownloads";
import { DateInput } from "./ui/date-input";

const today = () => new Date().toISOString().slice(0, 10);

// Πρόταση επόμενου Αρ. Πράξης (ίδιο βιβλίο ανά τύπο/έτος/κατάστημα)
const useNextActNumber = (caseData, user, open, current) => {
  const [num, setNum] = useState("");
  useEffect(() => {
    if (!open || !caseData) return;
    if (current) { setNum(String(current)); return; }
    const year = String(caseData.gak_year || new Date().getFullYear());
    axios.get(`${API}/pretrial-sectors/next-act-number`, { params: { court_type: caseData.court_type || "Μονομελές", year, branch: (user?.court_branch || "").trim() } })
      .then((r) => setNum(Number.isInteger(r.data?.next) ? String(r.data.next) : ""))
      .catch(() => setNum(""));
  }, [open, caseData, user, current]);
  return [num, setNum];
};

const ActFields = ({ actNum, setActNum, issueDate, setIssueDate, year, prefix }) => (
  <div className="grid grid-cols-2 gap-3">
    <div>
      <Label className="text-xs">Αρ. Πράξης <span className="text-red-600">*</span></Label>
      <Input value={actNum} onChange={(e) => setActNum(e.target.value)} placeholder={`π.χ. 12 (έτος ${year})`} className="h-9" data-testid={`${prefix}-act-number`} />
    </div>
    <div>
      <Label className="text-xs">Ημερομηνία Έκδοσης <span className="text-red-600">*</span></Label>
      <DateInput value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="h-9" required data-testid={`${prefix}-issue-date`} />
    </div>
  </div>
);

/** Ορισμός Δικαστή (Μονομελές) / Εισηγητή (Πολυμελές) με υποχρεωτική Ημερομηνία Έκδοσης → ορισμός + έκδοση & αποθήκευση Πράξης. */
export const JudgeAssignDialog = ({ open, onOpenChange, caseData, judges, user, onDone }) => {
  const isPoly = caseData?.court_type === "Πολυμελές";
  const [judgeId, setJudgeId] = useState("");
  const [issueDate, setIssueDate] = useState(today());
  const [includeNote, setIncludeNote] = useState(true);
  const [busy, setBusy] = useState(false);
  const year = String(caseData?.gak_year || new Date().getFullYear());
  const [actNum, setActNum] = useNextActNumber(caseData, user, open, isPoly ? caseData?.rapporteur_act_number : caseData?.assignment_act_number);
  useEffect(() => { if (open) { setJudgeId(""); setIssueDate(today()); } }, [open]);

  const confirm = async () => {
    const j = judges.find((x) => x.id === judgeId);
    if (!j) return toast.error("Επιλέξτε δικαστή");
    if (!issueDate) return toast.error("Η Ημερομηνία Έκδοσης είναι υποχρεωτική");
    if (!String(actNum).trim()) return toast.error("Δώστε Αρ. Πράξης");
    setBusy(true);
    try {
      await axios.post(`${API}/cases/${caseData.id}/assign-judge`, { judge_id: j.id, judge_name: j.full_name, judge_grade: j.grade, judge_email: j.email });
      const url = isPoly ? `${API}/pretrial-sectors/generate-rapporteur-act/${caseData.id}` : `${API}/pretrial-sectors/generate-act/${caseData.id}`;
      const params = isPoly ? { act_number: actNum.trim(), issue_date: issueDate, rapporteur_judge_id: j.id, composition_index: caseData.composition_index ?? undefined, pretrial_note: includeNote }
        : { act_number: actNum.trim(), issue_date: issueDate, pretrial_note: includeNote };
      await downloadActFromUrl(url, params, "docx", isPoly ? "Πράξη Ορισμού Εισηγητή" : "Πράξη Ορισμού Δικαστή");
      toast.success(`Ορίστηκε: ${j.full_name}${j.grade ? ` (${j.grade})` : ""}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ορισμού");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-blue-700" />{isPoly ? "Ορισμός Εισηγητή" : "Ορισμός Δικαστή"}</DialogTitle>
          <DialogDescription>Επιλέξτε Δικαστή Προδικαστικού ({caseData?.court_type}). Με τον ορισμό εκδίδεται και αποθηκεύεται η {isPoly ? "Πράξη Ορισμού Εισηγητή" : "Πράξη Ορισμού Δικαστή"} με την Ημερομηνία Έκδοσης που δηλώνετε.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <div>
            <Label className="text-xs">Δικαστής <span className="text-red-600">*</span></Label>
            <Select value={judgeId} onValueChange={setJudgeId}>
              <SelectTrigger data-testid="judge-picker-select"><SelectValue placeholder={judges.length ? "Επιλέξτε δικαστή..." : "Δεν υπάρχουν καταχωρημένοι δικαστές"} /></SelectTrigger>
              <SelectContent className="max-h-80">{judges.map((j) => <SelectItem key={j.id} value={j.id}><JudgeOptionLabel judge={j} /></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ActFields actNum={actNum} setActNum={setActNum} issueDate={issueDate} setIssueDate={setIssueDate} year={year} prefix="judge-assign" />
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer rounded-md border border-slate-200 bg-slate-50 p-2">
            <input type="checkbox" className="mt-0.5" checked={includeNote} onChange={(e) => setIncludeNote(e.target.checked)} data-testid="judge-assign-include-note" />
            <span>Συμπερίληψη της επισήμανσης «Επισημαίνεται ότι η νόμιμη προθεσμία διενέργειας του ανωτέρω προδικαστικού ελέγχου εκκινεί από …» (11η ημέρα μετά την Προσθήκη)</span>
          </label>
          <p className="text-xs text-slate-500">Με τον ορισμό ξεκινάει η προθεσμία 30 ημερών (Προδικαστικός Έλεγχος). Η Πράξη κατεβαίνει σε DOCX και αποθηκεύεται στα «Αποθηκευμένα Έγγραφα».</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Άκυρο</Button>
          <Button onClick={confirm} disabled={busy || !judgeId || !issueDate} className="bg-blue-700 hover:bg-blue-800" data-testid="judge-assign-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />} Ορισμός & Έκδοση Πράξης
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Ορισμός Σύνθεσης Πολυμελούς σε ΜΙΑ υπόθεση (καρτέλα) με υποχρεωτική Ημερομηνία Έκδοσης → Πράξη Ορισμού Σύνθεσης. */
export const CompositionAssignDialog = ({ open, onOpenChange, caseData, user, onDone }) => {
  const [options, setOptions] = useState([]);
  const [pick, setPick] = useState("");
  const [issueDate, setIssueDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const year = String(caseData?.gak_year || new Date().getFullYear());
  const [actNum, setActNum] = useNextActNumber(caseData, user, open, caseData?.assignment_act_number);
  const hearingDate = (caseData?.last_hearing_date || "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
  const hd = hearingDate ? `${hearingDate[3]}-${hearingDate[2]}-${hearingDate[1]}` : "";

  useEffect(() => {
    if (!open) return;
    setPick(""); setIssueDate(today());
    axios.get(`${API}/compositions/options`, { params: { court_type: "Πολυμελές", hearing_date: hd } })
      .then((r) => setOptions(r.data?.compositions || [])).catch(() => setOptions([]));
  }, [open, hd]);

  const chosen = options.find((o) => `${o.sector_id}:${o.composition_index}` === pick);
  const confirm = async () => {
    if (!chosen) return toast.error("Επιλέξτε Σύνθεση");
    if (!issueDate) return toast.error("Η Ημερομηνία Έκδοσης είναι υποχρεωτική");
    if (!String(actNum).trim()) return toast.error("Δώστε Αρ. Πράξης");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cases/${caseData.id}/composition`, { sector_id: chosen.sector_id, composition_index: chosen.composition_index, exclude_members: chosen.conflicts || [] });
      toast.success(r.data?.message || "Ορίστηκε η Σύνθεση");
      await downloadActFromUrl(`${API}/pretrial-sectors/generate-act/${caseData.id}`, { act_number: actNum.trim(), issue_date: issueDate, composition_index: chosen.composition_index }, "docx", "Πράξη Ορισμού Σύνθεσης");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Σφάλμα ορισμού Σύνθεσης", { duration: 10000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-purple-700" />Ορισμός Σύνθεσης Πολυμελούς</DialogTitle>
          <DialogDescription>Επιλέξτε Σύνθεση του Τομέα. Με τον ορισμό εκδίδεται και αποθηκεύεται η Πράξη Ορισμού Σύνθεσης (άρθρο 237 παρ. 2 ΚΠολΔ) με την Ημερομηνία Έκδοσης που δηλώνετε.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <div>
            <Label className="text-xs">Σύνθεση <span className="text-red-600">*</span></Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="composition-assign-select"><SelectValue placeholder={options.length ? "Επιλέξτε Σύνθεση..." : "Δεν υπάρχουν Συνθέσεις — ορίστε τις στους Τομείς"} /></SelectTrigger>
              <SelectContent className="max-h-80">
                {options.map((o) => (
                  <SelectItem key={`${o.sector_id}:${o.composition_index}`} value={`${o.sector_id}:${o.composition_index}`} data-testid={`composition-assign-option-${o.sector_number}-${o.composition_index}`}>
                    {o.sector_number}ο Τμήμα · {o.label} — Πρόεδρος: {o.president || "—"} ({(o.members || []).length} μέλη){o.conflicts?.length ? ` · ⚠ ${o.conflicts.length} σύγκρουση` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {chosen && (
            <div className="rounded border border-purple-200 bg-purple-50/40 p-2 text-xs space-y-1" data-testid="composition-assign-members">
              {(chosen.members || []).map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${m.role === "alternate" ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}`}>{m.role === "alternate" ? "Αναπλ." : "Τακτ."}</Badge>
                  <span className="font-medium">{m.name}</span><span className="text-slate-500">{m.grade}</span>
                  {m.email && <span className="text-slate-400">· {m.email}</span>}
                  {m.mono_conflict && <span className="text-red-600">⚠ Μονομελές ίδια ημέρα ({m.mono_conflict}) — θα εξαιρεθεί</span>}
                </div>
              ))}
            </div>
          )}
          <ActFields actNum={actNum} setActNum={setActNum} issueDate={issueDate} setIssueDate={setIssueDate} year={year} prefix="composition-assign" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Άκυρο</Button>
          <Button onClick={confirm} disabled={busy || !chosen || !issueDate} className="bg-purple-700 hover:bg-purple-800" data-testid="confirm-composition-assignment-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Users className="w-4 h-4 mr-1" />} Ορισμός & Έκδοση Πράξης
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
